import type { UseQueryOptions } from '@tanstack/react-query'
import { type QueryStatus, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { settingsService } from 'v2/shared/services/settings-service'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type {
  IndiaKycSignatoryPayload,
  IndividualKycConsentPayload,
  IndividualKycPanPayload,
  RIndiaKycBusiness,
  RIndividualKycState,
} from 'v2/types/api/india-kyc'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { inReviewPollMs } from '../number-compliance-v2/queries-v2'
import { isPersonalPan } from './pan'

const INDIA_KYC_POLL_MS = 3_000
// core asks Perfios about an open DigiLocker link at most every 15s, so it is re-read at that pace,
// except for a minute after the return page signals: core asks at once but answers before Perfios.
const DIGILOCKER_POLL_MS = 15_000
const DIGILOCKER_DONE_WINDOW_MS = 60_000
// The signatory has 7 days, so an open business KYC is re-read every minute.
const BUSINESS_KYC_POLL_MS = 60_000

/** A step core finishes in seconds; a failing read backs off rather than retrying every 3s. */
export const indiaKycStepPollMs = (status: QueryStatus): number =>
  status === 'error' ? INDIA_KYC_POLL_MS * 10 : INDIA_KYC_POLL_MS

/** A business KYC the signatory has open: no second one can start (core: business_in_progress). */
export const isBusinessKycOpen = (business: RIndiaKycBusiness | null | undefined): boolean =>
  business?.status === 'in_progress' || business?.status === 'review'

// Window-focus refetch is off, so the state is polled while it can move with no request from this
// page: core's DigiLocker check and checks, a manual review, the signatory's business KYC.
export const indiaKycRefetchMs = (
  state: RIndividualKycState | undefined,
  status: QueryStatus,
  digiLockerDoneAt?: number,
): number | false => {
  if (state?.status === 'in_progress' && state.step === 'checking') {
    return indiaKycStepPollMs(status)
  }
  if (state?.status === 'in_progress' && state.step === 'digilocker') {
    if (digiLockerDoneAt && Date.now() - digiLockerDoneAt < DIGILOCKER_DONE_WINDOW_MS) {
      return indiaKycStepPollMs(status)
    }
    // An expired link stays put until the person asks for a new one.
    if (state.code === 'digilocker_expired') return false
    return status === 'error' ? indiaKycStepPollMs(status) : DIGILOCKER_POLL_MS
  }
  const business = state?.business
  if (
    state?.status === 'review' ||
    business?.status === 'review' ||
    business?.step === 'finalizing'
  ) {
    return inReviewPollMs(status)
  }
  return isBusinessKycOpen(business) ? BUSINESS_KYC_POLL_MS : false
}

/** core answers the console's India KYC routes only for UCC View. */
export const INDIA_KYC_PERMISSION = { module: RbacModules.ucc, type: RbacPermissionTypes.view }

/** The hosted page has no permission map: hodor's session decides there. */
export const useCanUseIndiaKyc = (): boolean => {
  const { hasPermission } = usePermissions()
  return isEmbedMode() || hasPermission(INDIA_KYC_PERMISSION.module, INDIA_KYC_PERMISSION.type)
}

export interface IndiaKycStateOptions
  extends Pick<UseQueryOptions<RIndividualKycState>, 'retry' | 'retryOnMount' | 'meta'> {
  /** When the DigiLocker return page last signalled, from Date.now(). */
  digiLockerDoneAt?: number
  /**
   * Re-read while the state can move. A read is slow in core, so only a page showing the KYC
   * polls; any other reader keeps its read for the client's staleTime (a minute).
   */
  poll?: boolean
}

// Without the permission it stays idle, so it reads as no KYC rather than as an outage.
export const useIndiaKycStateQuery = (
  enabled: boolean,
  { digiLockerDoneAt, poll = false, ...options }: IndiaKycStateOptions = {},
) => {
  const canUse = useCanUseIndiaKyc()
  return useQuery({
    queryKey: getSettingsQueryKeys().indiaKycState(),
    enabled: enabled && canUse,
    queryFn: async () => (await settingsService.getIndiaKyc()).data,
    refetchInterval: poll
      ? (query) => indiaKycRefetchMs(query.state.data, query.state.status, digiLockerDoneAt)
      : false,
    // A failed read is not re-sent by each screen that mounts on it: a page that swaps its loader
    // for one would re-read, and fail, in a loop.
    retryOnMount: false,
    ...options,
  })
}

// What core's payload guard and schemas say for `<`, `>` or a control character in a field.
const UNSAFE_CHARS_MESSAGE = "must not contain '<', '>', or control characters"

// core's `errors.global_error`. Field errors carrying the payload guard's message are named
// `unsafe_characters`; any other field error (a schema refusing a drifted payload) has no code.
export const indiaKycErrorCode = (error: unknown): string | undefined => {
  // axios 0.21's isAxiosError throws on null, which is a mutation's error until it fails.
  if (!error || !axios.isAxiosError(error)) return undefined
  const errors = (error.response?.data as { errors?: unknown } | undefined)?.errors
  if (!errors || typeof errors !== 'object') return undefined
  const { global_error: code, ...fields } = errors as Record<string, unknown>
  if (typeof code === 'string') return code
  const isUnsafe = Object.values(fields).some(
    (messages) => Array.isArray(messages) && messages.includes(UNSAFE_CHARS_MESSAGE),
  )
  return isUnsafe ? 'unsafe_characters' : undefined
}

/** 409: out of order, or already done elsewhere (another tab). The state knows where we are. */
export const isConflictError = (error: unknown): boolean =>
  !!error && axios.isAxiosError(error) && error.response?.status === 409

// Each step answers with the new state, cached as is: a refetch would leave the old step on screen.
// An `isStale` error (409 by default) re-reads it. Under indiaKycBase, so it is never persisted.
export const useIndiaKycStepMutation = <TPayload, TState = RIndividualKycState>(
  request: (payload: TPayload) => Promise<{ data: TState }>,
  stateKey = getSettingsQueryKeys().indiaKycState(),
  isStale: (error: unknown) => boolean = isConflictError,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: getSettingsQueryKeys().indiaKycBase,
    mutationFn: async (payload: TPayload) => (await request(payload)).data,
    onSuccess: (state) => queryClient.setQueryData(stateKey, state),
    onError: (error) => {
      if (isStale(error)) {
        void queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().indiaKycBase })
      }
    },
  })
}

export const useIndividualKycPanMutation = () =>
  useIndiaKycStepMutation((payload: IndividualKycPanPayload) =>
    settingsService.checkIndividualKycPan(payload),
  )

// Not a step: core answers with checks, not a KYC state. Under indiaKycBase, never persisted, and
// dropped off screen, as support can free a PAN in use; Continue re-runs every check anyway.
export const useIndividualKycPanPrecheckQuery = (pan: string) => {
  // A disabled query still caches its key: one idle entry, not one per character typed.
  const checkedPan = isPersonalPan(pan) ? pan : ''
  return useQuery({
    queryKey: getSettingsQueryKeys().indiaKycPanPrecheck(checkedPan),
    queryFn: async () =>
      (await settingsService.precheckIndividualKycPan(checkedPan)).data.pan_checks,
    enabled: !!checkedPan,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    meta: { silent: true },
  })
}

export const useIndividualKycConsentMutation = () =>
  useIndiaKycStepMutation((payload: IndividualKycConsentPayload) =>
    settingsService.saveIndividualKycConsent(payload),
  )

export const useIndividualKycDigiLockerMutation = () =>
  useIndiaKycStepMutation(() => settingsService.startIndividualKycDigiLocker())

export const useIndividualKycDigiLockerDoneMutation = () =>
  useIndiaKycStepMutation(() => settingsService.completeIndividualKycDigiLocker())

export const useBusinessKycSignatoryMutation = () =>
  useIndiaKycStepMutation((payload: IndiaKycSignatoryPayload) =>
    settingsService.inviteIndiaKycSignatory(payload),
  )

export const useBusinessKycResendMutation = () =>
  useIndiaKycStepMutation(() => settingsService.resendIndiaKycSignatory())

export const useBusinessKycCancelMutation = () =>
  useIndiaKycStepMutation(() => settingsService.cancelIndiaKycBusiness())
